// Unified Text-to-Speech engine for PlayMaster IA.
// Single source of truth for voice selection, locale binding and prosody.
//
// Design:
//  - The user's UI language (from LangContext) is the ONLY source of truth.
//    No auto-detection from text content. No mid-utterance language change.
//  - Voices are preloaded asynchronously so the first utterance never falls
//    back to the OS default (which on Spanish machines was speaking ES even
//    when text was English).
//  - We pick a stable MALE voice per language with sensible fallbacks.
//  - Long text is split into sentence chunks bound to the same voice for
//    smoother, more natural prosody and to keep the engine from picking a
//    different voice mid-paragraph on some browsers.

const LANG_MAP = {
  es: 'es-ES', en: 'en-US', fr: 'fr-FR', pt: 'pt-BR', zh: 'zh-CN', ar: 'ar-SA',
};

// Preferred male voices per locale (substring match on voice.name, case-insensitive).
// Order = priority. Most browsers expose at least one of these.
const MALE_VOICES = {
  es: ['Google español masculino', 'Diego', 'Jorge', 'Paco', 'Carlos', 'Enrique', 'Pablo', 'Juan', 'Microsoft Pablo', 'Microsoft Sabina', 'Spanish'],
  en: ['Google US English Male', 'Microsoft David', 'Microsoft Mark', 'Microsoft Guy', 'Daniel', 'Alex', 'Fred', 'Aaron', 'Tom', 'Google UK English Male'],
  fr: ['Google français masculin', 'Thomas', 'Mathieu', 'Microsoft Paul', 'Microsoft Henri', 'Nicolas'],
  pt: ['Google português do Brasil', 'Luciano', 'Daniel', 'Microsoft Daniel', 'Felipe', 'Eddy'],
  zh: ['Google 普通话（中国大陆）', 'Yunyang', 'Kangkang', 'Microsoft Yunyang', 'Microsoft Kangkang', 'Tingting'],
  ar: ['Google عربي', 'Hamed', 'Tarik', 'Microsoft Hamed', 'Maged', 'Naayf'],
};

let _voicesReady = false;
let _voicesReadyPromise = null;
let _selectedVoiceCache = {}; // { langCode: SpeechSynthesisVoice }

function loadVoices() {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return [];
  return window.speechSynthesis.getVoices() || [];
}

/** Wait until the browser has actually populated its voice list.
 *  Chrome/Edge load voices async on first call; mobile Safari fires
 *  `voiceschanged` after the first user gesture. */
export function ensureVoicesLoaded() {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
    return Promise.resolve([]);
  }
  if (_voicesReady) return Promise.resolve(loadVoices());
  if (_voicesReadyPromise) return _voicesReadyPromise;

  _voicesReadyPromise = new Promise((resolve) => {
    const tryResolve = () => {
      const v = loadVoices();
      if (v.length > 0) {
        _voicesReady = true;
        resolve(v);
        return true;
      }
      return false;
    };
    if (tryResolve()) return;
    const handler = () => { if (tryResolve()) window.speechSynthesis.removeEventListener('voiceschanged', handler); };
    window.speechSynthesis.addEventListener('voiceschanged', handler);
    // Fallback: resolve after 2.5s even if list still empty (older browsers)
    setTimeout(() => { _voicesReady = true; resolve(loadVoices()); }, 2500);
  });
  return _voicesReadyPromise;
}

function pickVoice(voices, langCode) {
  const want = LANG_MAP[langCode] || 'es-ES';
  const wantBase = want.split('-')[0];

  // 1) Try preferred MALE names that also match the locale
  const prefList = MALE_VOICES[langCode] || [];
  for (const name of prefList) {
    const v = voices.find(v =>
      v.name.toLowerCase().includes(name.toLowerCase()) &&
      (v.lang.toLowerCase().startsWith(wantBase) || v.lang.toLowerCase() === want.toLowerCase())
    );
    if (v) return v;
  }

  // 2) Any voice with our exact locale, preferring localService/enhanced
  const exact = voices.filter(v => v.lang.toLowerCase() === want.toLowerCase());
  if (exact.length) {
    const enhanced = exact.find(v => /enhanced|premium|natural/i.test(v.name));
    return enhanced || exact[0];
  }

  // 3) Any voice with the same base language (e.g. en-* for en)
  const baseMatch = voices.filter(v => v.lang.toLowerCase().startsWith(wantBase));
  if (baseMatch.length) {
    const enhanced = baseMatch.find(v => /enhanced|premium|natural/i.test(v.name));
    return enhanced || baseMatch[0];
  }

  // 4) Last resort: anything (will sound wrong but won't crash)
  return voices[0] || null;
}

async function getVoiceFor(langCode) {
  if (_selectedVoiceCache[langCode]) return _selectedVoiceCache[langCode];
  const voices = await ensureVoicesLoaded();
  const v = pickVoice(voices, langCode);
  if (v) _selectedVoiceCache[langCode] = v;
  return v;
}

/** Warm up the TTS engine on the FIRST user gesture (button click etc.).
 *  Required by iOS Safari & some Android browsers, AND fixes the
 *  "first sentence speaks Spanish even on English" Chromium quirk. */
export async function primeTTS(langCode) {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
  try {
    // Trigger voice list load if not already loaded
    ensureVoicesLoaded();
    const voice = await getVoiceFor(langCode);
    const u = new SpeechSynthesisUtterance(' ');
    u.lang = LANG_MAP[langCode] || 'es-ES';
    if (voice) u.voice = voice;
    u.volume = 0;
    u.rate = 1;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  } catch { /* ignore */ }
}

/** Cancel any ongoing speech immediately. Safe to call anywhere. */
export function cancelTTS() {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
  try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
}

/** Split long text into sentence chunks. Each chunk is spoken with the
 *  same voice/lang so prosody stays consistent and the browser can't
 *  switch voice mid-utterance. */
function chunkSentences(text) {
  if (!text) return [];
  // Normalize whitespace
  const clean = String(text).replace(/\s+/g, ' ').trim();
  if (clean.length === 0) return [];
  // Split on . ! ? 。 ! ？ keeping the punctuation
  const parts = clean.match(/[^.!?。！？]+[.!?。！？]?/g) || [clean];
  return parts.map(s => s.trim()).filter(Boolean);
}

/** Speak `text` in the given UI language with a stable male voice.
 *  Returns a cancellation function. */
export async function speak({ text, lang, onEnd, onStart, onError, rate = 0.88, pitch = 1.0 }) {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
    onEnd?.(); return () => {};
  }
  const langCode = (lang || 'es').toLowerCase();
  const sentences = chunkSentences(text);
  if (sentences.length === 0) { onEnd?.(); return () => {}; }

  // Cancel anything that was going on first to guarantee clean start
  cancelTTS();
  const voice = await getVoiceFor(langCode);
  const speechLang = LANG_MAP[langCode] || 'es-ES';

  let cancelled = false;
  let currentIndex = 0;

  const speakNext = () => {
    if (cancelled || currentIndex >= sentences.length) {
      if (!cancelled) onEnd?.();
      return;
    }
    const u = new SpeechSynthesisUtterance(sentences[currentIndex]);
    u.lang = speechLang;
    if (voice) u.voice = voice;
    u.rate = rate;   // ~0.88 = slightly slower than default, more natural pace
    u.pitch = pitch; // 1.0 = natural inflection
    u.volume = 1.0;
    if (currentIndex === 0) u.onstart = () => onStart?.();
    u.onend = () => { currentIndex += 1; speakNext(); };
    u.onerror = (e) => { if (!cancelled) { onError?.(e); onEnd?.(); } };
    try {
      window.speechSynthesis.speak(u);
    } catch (e) {
      onError?.(e); onEnd?.();
    }
  };

  speakNext();

  return () => { cancelled = true; cancelTTS(); };
}

/** Returns the cached selected voice (for debugging / UI hints). */
export function getSelectedVoiceName(langCode) {
  return _selectedVoiceCache[langCode]?.name || null;
}
